/* eslint-disable import/no-unresolved */
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const LEVEL_COLORS = { 상: '#10b981', 중: '#f59e0b', 하: '#ef4444' };

const EMPTY = {
  total: 0,
  performance: { 상: 0, 중: 0, 하: 0 },
  engagement: { 상: 0, 중: 0, 하: 0 },
  satisfaction: { 상: 0, 중: 0, 하: 0 },
};

const toChartData = (summary) => [
  { name: '수행도', ...summary.performance },
  { name: '참여도', ...summary.engagement },
  { name: '만족도', ...summary.satisfaction },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <p style={{ fontWeight: 'bold', margin: '0 0 4px' }}>{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.fill, margin: 0 }}>{p.dataKey} {p.value}회</p>
      ))}
    </div>
  );
};

const PiCarePerformanceWidget = () => {
  const { performanceSummary, loading } = useSelector((state) => state.picareDashboard);
  const summary = performanceSummary || EMPTY;
  const data = useMemo(() => toChartData(summary), [summary]);

  if (loading.performanceSummary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (summary.total === 0) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: 14, gap: 4 }}>
        <span>데이터 없음</span>
        <span style={{ fontSize: 12 }}>세션 데이터가 쌓이면 표시됩니다</span>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', padding: '0 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px', marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>최근 2개월 · 총 {summary.total}회 세션</span>
        <div style={{ display: 'flex', gap: 12 }}>
          {Object.entries(LEVEL_COLORS).map(([level, color]) => (
            <span key={level} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b' }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: color }} />
              {level}
            </span>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart layout="vertical" data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
            <YAxis type="category" dataKey="name" width={44} tick={{ fontSize: 12, fontWeight: 'bold' }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Bar dataKey="상" stackId="a" fill={LEVEL_COLORS['상']} />
            <Bar dataKey="중" stackId="a" fill={LEVEL_COLORS['중']} />
            <Bar dataKey="하" stackId="a" fill={LEVEL_COLORS['하']} radius={[0, 3, 3, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PiCarePerformanceWidget;
